import { FunctionComponent, useEffect, useState } from 'react'
import { SuggestSettingsResponse, SuggestStaffProfile } from '@/types/Suggest'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useLazyQuery } from '@apollo/client'
import { GET_SUGGEST_STAFFS } from '@/modules/API/suggest/suggestHandler'
import { Daycare } from '@/types/Daycare'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import CitySelect from '@/components/_custom/CitySelect/citySelect'
import UserInput from '@/components/_custom/UserInput/userInput'
import styles from './CALDStaffSettings.module.scss'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import { GET_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'

interface CALDStaffSettingsProps {
    daycare: Daycare
    setAllSuggest: (allSuggest: SuggestStaffProfile[]) => void
    setCALDRequest: (CALDRequest: boolean) => void
}

const CALDStaffSettings: FunctionComponent<CALDStaffSettingsProps> = ({
    daycare,
    setAllSuggest,
    setCALDRequest
}: CALDStaffSettingsProps) => {
    const { t } = useTranslation()

    const [country, setCountry] = useState<string>(daycare?.country || '')
    const [city, setCity] = useState<string>(daycare?.city || '')
    const [range, setRange] = useState<string>('0')

    const [getSuggestSettings, { data: dataSuggestSettings }] = useLazyQuery<{
        getSuggestSettings: SuggestSettingsResponse
    }>(GET_SUGGEST_SETTINGS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (dataSuggestSettings) {
            setRange(dataSuggestSettings.getSuggestSettings.staffDistance.split('_')[1])
        }
    }, [dataSuggestSettings])

    useEffect(() => {
        setCountry(daycare?.country || '')
        setCity(daycare?.city || '')
        getSuggestSettings()
    }, [daycare])

    const [
        getSuggestParent,
        { loading: loadingGetSuggestParent, data: dataGetSuggestParent, error: errorGetSuggestParent }
    ] = useLazyQuery(GET_SUGGEST_STAFFS)

    useEffect(() => {
        if (dataGetSuggestParent) {
            setAllSuggest(dataGetSuggestParent.getSuggestStaffs)
            setCALDRequest(true)
        }
    }, [dataGetSuggestParent])

    useEffect(() => {
        if (errorGetSuggestParent) {
            setAllSuggest([])
            setCALDRequest(false)
        }
    }, [errorGetSuggestParent])

    function handleSearch(): void {
        getSuggestParent({
            variables: {
                daycareId: daycare?.id,
                country: country,
                city: city,
                range: Number(range)
            }
        })
    }

    return (
        <>
            {loadingGetSuggestParent && <Loading overall />}
            <div className={styles.Container}>
                <div className={styles.UserInput}>
                    <CountrySelect country={country} setCountry={setCountry} />
                </div>
                <div className={styles.UserInput}>
                    <CitySelect city={city} setCity={setCity} country={daycare?.country || 'FR'} />
                </div>

                <UserInput
                    type={'number'}
                    value={range}
                    setValue={setRange}
                    format={'num'}
                    minNumber={0}
                    maxNumber={1000}
                    verify={'number'}
                    className={styles.UserInput}
                    label={t('inRadiusOf')}
                    endAdornment={'km'}
                />
                <ButtonTextIcon type={'confirm'} onClick={handleSearch} className={styles.Button} />
            </div>
            {errorGetSuggestParent && <ErrorMessage error={t('generalError')} />}
        </>
    )
}

export default CALDStaffSettings
